<script setup lang="ts">
import { ref } from 'vue';
import { useRoute } from 'vue-router';
import { Feature, PageName } from '@/api/tracking';
import Avatar from '@/components/common/atoms/avatar.vue';
import ArticleComment from '@/components/common/comment/article-comment.vue';
import MentionAutocompleteTextarea from '@/components/common/molecules/mention-autocomplete-textarea.vue';
import { AdpDocument, Comment } from '@/types';
import { useComment } from '@/utils/actions/comment';
import { useUserInfo } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';

interface Props {
  comments?: Comment[];
  omitting?: boolean;
  adpDocument?: AdpDocument;
  showAvatar?: boolean;
  showReplyComment?: boolean;
  showReplyButton?: boolean;
  pageName: PageName;
  feature?: Feature;
  isSummaryGroup?: boolean;
  showCommentWidget?: boolean;
  groupId?: number;
  foldReplies?: boolean;
  showJoinGroupButton?: boolean;
  showReaction?: boolean;
  showCommentMenu?: boolean;
  disabled?: boolean;
  isNarrow?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  comments: () => [],
  omitting: false,
  showAvatar: true,
  showReplyComment: true,
  showReplyButton: true,
  isSummaryGroup: false,
  showCommentWidget: false,
  foldReplies: true,
  showJoinGroupButton: false,
  showReaction: true,
  showCommentMenu: true,
  disabled: false,
  isNarrow: false,
});

const emit = defineEmits<{
  'edit-comment': [comment: Comment];
  'delete-comment': [comment: Comment];
}>();

const route = useRoute();
const emitter = useEmitter();

// NOTE: 計測ログなど実行時にroute.fullPathを使用すると、
// route.fullPathで取得する前に画面遷移など行われて、
// 想定と異なるパスが取得される可能性があるため定数で保持する
const PAGE_URL = route.fullPath;

// showCommentWidget=trueはadp-documentしかない
// TODO: コメント処理の移動でadp-documentに移す
const { createComment } = useComment({
  trackingBaseData: {
    pageName: props.pageName,
    feature: props.feature,
    pageUrl: PAGE_URL,
  },
});

const { data: userInfo } = useUserInfo();
const commentRef = ref<InstanceType<typeof MentionAutocompleteTextarea>>();

const isCommentTextEmpty = (): boolean => {
  return (commentRef.value?.trimmedComment ?? '').length === 0;
};
const showGroupName = (index: number) => {
  if (!props.isSummaryGroup) return true;
  return index === 0;
};

const callPostComment = () => {
  commentRef.value?.postComment();
};

const postComment = async () => {
  if (commentRef.value) {
    const createdComment = await createComment({
      groupId: props.groupId!,
      adpDocument: props.adpDocument,
      content: commentRef.value?.trimmedComment,
      mention: commentRef.value?.mention,
    });

    emitter.emit('article-updated', props.adpDocument);
    emitter.emit('comment-created', {
      ...createdComment,
    });
    commentRef.value?.resetTrimmedComment();
  }
};
</script>

<template>
  <div class="m-comment-list">
    <div
      class="m-comment-list-body"
      v-for="(comment, index) in comments"
      :key="comment.id"
    >
      <ArticleComment
        :article="adpDocument"
        :comment="comment"
        :omitting="omitting"
        :show-avatar="showAvatar"
        :show-reply-comment="showReplyComment"
        :show-group="showGroupName(index)"
        :show-reply-button="showReplyButton"
        :is-summary-group="isSummaryGroup"
        :show-join-group-button="showJoinGroupButton"
        :feature="feature"
        :page-name="pageName"
        :fold-replies="foldReplies"
        :show-reaction="showReaction"
        :show-comment-menu="showCommentMenu"
        :is-narrow="isNarrow"
        @delete-comment="c => emit('delete-comment', c)"
        @edit-comment="c => emit('edit-comment', c)"
      ></ArticleComment>
    </div>
    <div class="comment-widget" v-if="userInfo && showCommentWidget">
      <router-link :to="`/users/${userInfo.id}`">
        <Avatar size="m" :image-url="userInfo.image_url"></Avatar>
      </router-link>
      <div class="spacing-08"></div>
      <MentionAutocompleteTextarea
        ref="commentRef"
        placeholder="コメントする..."
        :min-height="40"
        :rows="1"
        :auto-focus="false"
        :disabled="disabled"
        @post-comment="postComment"
      ></MentionAutocompleteTextarea>
      <div class="spacing-08"></div>
      <button
        class="comment-button-small c-btn c-btn--small c-btn--AnewsPrimary"
        :class="{ disabled: isCommentTextEmpty() }"
        :disabled="isCommentTextEmpty()"
        @click="callPostComment"
      >
        投稿
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
div.m-comment-list {
  display: flex;
  flex-direction: column;
}
.comment-widget {
  display: flex;
  align-items: center;
  margin-top: 8px;
}
.comment-button-small {
  width: 60px;
  height: 40px;
  padding: 0;
}
</style>
